/**
 * @fileoverview State, effects, and handlers for the breakfast event form.
 * @module hooks/useBreakfastEventForm
 */
import { use, useRef, useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { AppContext } from "../context/AppContext.js";
import { logger, sanitizeError } from "../utils/logger.js";
import { validateBusinessEventName } from "../utils/common.js";
import { canManageBreakfastEvents } from "../utils/accessControl";
import useRequireAccess from "./useRequireAccess";
import {
  submitBreakfastEvent,
  initializeBreakfastEventForm,
  getAllBreakfastEvents,
} from "../logic/breakfastEvents/index.js";
import { getUsersFromSpecificRole } from "../logic/user/index.js";
import styles from "../styles/BreakfastEventForm.module.css";
import {
  AUDIENCE_MEMBERS_KEY,
  AUDIENCE_NON_MEMBERS_KEY,
  BANNER_MIME_TYPES,
  BREAKFAST_EVENTS_PATH,
  DATE_FORMAT,
  HOST_ROLE_ID,
  INITIAL_BREAKFAST_FORM,
  MAX_BANNER_BYTES,
  UNEXPECTED_SUBMIT_RESULT,
  formatBreakfastSubmitPayload,
  isSaveDisabled,
  nextBreakfastAudience,
} from "../pages/breakfastEventFormModel.js";

/**
 * The form does not call the breakfast APIs when localStorage has no token or the literal string "undefined".
 */
const hasAuthToken = () => {
  const token = localStorage.getItem("token");
  return Boolean(token) && token.trim() !== "undefined";
};

/**
 * Drops a rejected banner. Both the type error and the size error clear the preview, the file name, and the MIME type.
 */
const clearBannerSelection = (setters, message) => {
  setters.setError(message);
  setters.setImageChanged(false);
  setters.setFormData((prev) => ({ ...prev, imagenBanner: null }));
  setters.setImageName(null);
  setters.setImageFormat(null);
};

/**
 * Create navigates with `eventCreated`. Update navigates with `eventUpdated`.
 * Any other result is logged and leaves the user on the form.
 */
const navigateAfterSubmit = (result, eventId, navigate) => {
  if (result?.ok && result.wasCreate) {
    navigate(BREAKFAST_EVENTS_PATH, { state: { eventCreated: true } });
    return;
  }
  if (result?.ok && eventId) {
    navigate(BREAKFAST_EVENTS_PATH, { state: { eventUpdated: true } });
    return;
  }
  logger.warn(UNEXPECTED_SUBMIT_RESULT, { data: result });
};

/**
 * Handlers read the latest form API from a ref.
 * That keeps each callback stable for the view without copying the argument list into every dependency array.
 * Image reads happen at click time, so a stale `formData` closure is not used.
 */
const useBreakfastFormHandlers = (api) => {
  const apiRef = useRef(api);
  apiRef.current = api;

  /**
   * Stores a banner when the file is jpeg, jpg, or png and within 5 MiB.
   * Any other file clears the preview.
   * @param {Event} event - File input change event.
   * @returns {void}
   */
  const handleImageChange = useCallback((event) => {
    const file = event.target.files?.[0];
    const current = apiRef.current;
    if (!file) {
      return;
    }
    const setters = {
      setError: current.setError,
      setImageChanged: current.setImageChanged,
      setFormData: current.setFormData,
      setImageName: current.setImageName,
      setImageFormat: current.setImageFormat,
    };
    if (!BANNER_MIME_TYPES.includes(file.type)) {
      clearBannerSelection(
        setters,
        current.t("pages.buyerCompanyForm.logoFileTypeError"),
      );
      return;
    }
    if (file.size > MAX_BANNER_BYTES) {
      clearBannerSelection(
        setters,
        current.t("pages.buyerCompanyForm.logoFileSizeError"),
      );
      return;
    }
    const reader = new FileReader();
    reader.onload = (loadEvent) => {
      current.setImageChanged(true);
      current.setFormData((prev) => ({
        ...prev,
        imagenBanner: loadEvent.target.result,
      }));
      current.setImageName(file.name);
      current.setImageFormat(file.type);
      current.setHasChanges(true);
    };
    reader.readAsDataURL(file);
  }, []);

  /**
   * Submits the breakfast event and navigates to the list on create or update.
   * @param {Event} event - Form submit event.
   * @returns {Promise<void>}
   */
  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    const current = apiRef.current;
    current.setShowError(false);
    try {
      const eventToSubmit = formatBreakfastSubmitPayload({
        formData: current.formData,
        imageChanged: current.imageChanged,
        imageName: current.imageName,
        imageFormat: current.imageFormat,
        userId: localStorage.getItem("userId"),
        formatDate: (value) => dayjs(value).format(DATE_FORMAT),
      });
      const result = await submitBreakfastEvent(
        eventToSubmit,
        current.eventData?._id,
        current.navigate,
        current.setSaving,
        current.setEventData,
        current.setHasChanges,
      );
      navigateAfterSubmit(result, current.eventData?._id, current.navigate);
    } catch (err) {
      logger.error("Error", { err: sanitizeError(err) });
      current.setShowError(true);
    }
  }, []);

  /**
   * Writes one form field. Re-checks the event name against the loaded list when that field changes.
   * @param {Event} event - Input change event.
   * @returns {void}
   */
  const handleInputChange = useCallback((event) => {
    const { name, value } = event.target;
    const current = apiRef.current;
    current.setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "nombre" && current.allBreakfastEvents.length > 0) {
      current.setNameValidation(
        validateBusinessEventName(
          value,
          current.allBreakfastEvents,
          current.eventData?._id,
        ),
      );
    }
    current.setHasChanges(true);
  }, []);

  /**
   * Toggles one audience checkbox and stores the resulting audience code.
   * @param {string} which - `socios` or `noSocios`.
   * @returns {void}
   */
  const handleAudienciaCheckboxChange = useCallback((which) => {
    const current = apiRef.current;
    current.setFormData((prev) => ({
      ...prev,
      audiencia: nextBreakfastAudience(prev.audiencia, which),
    }));
    current.setHasChanges(true);
  }, []);

  /**
   * Returns to the previous page.
   * @returns {void}
   */
  const handleGoBack = useCallback(() => {
    apiRef.current.navigate(-1);
  }, []);

  /**
   * Stores the selected host's user id.
   * @param {{_id: string}|null} selectedUser - Host chosen in the select.
   * @returns {void}
   */
  const handleHostChange = useCallback((selectedUser) => {
    const current = apiRef.current;
    current.setFormData((prev) => ({
      ...prev,
      idUsuarioAnfitrion: selectedUser?._id,
    }));
    current.setHasChanges(true);
  }, []);

  /**
   * Sets whether the event is active.
   * @param {Event} event - Switch change event.
   * @returns {void}
   */
  const handleActivoChange = useCallback((event) => {
    const current = apiRef.current;
    current.setHasChanges(true);
    current.setFormData((prev) => ({
      ...prev,
      activo: event.target.checked,
    }));
  }, []);

  /**
   * Toggles the members side of the audience.
   * @returns {void}
   */
  const handleSociosAudience = useCallback(() => {
    handleAudienciaCheckboxChange(AUDIENCE_MEMBERS_KEY);
  }, [handleAudienciaCheckboxChange]);

  /**
   * Toggles the non-members side of the audience.
   * @returns {void}
   */
  const handleNonMembersAudience = useCallback(() => {
    handleAudienciaCheckboxChange(AUDIENCE_NON_MEMBERS_KEY);
  }, [handleAudienciaCheckboxChange]);

  /**
   * Opens the partner modal without submitting the event form.
   * @param {Event} event - Button click event.
   * @returns {void}
   */
  const handleOpenPartners = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    apiRef.current.setShowAddPartnersModal(true);
  }, []);

  /**
   * Closes the partner modal without changing partner filters.
   * @returns {void}
   */
  const handleClosePartners = useCallback(() => {
    apiRef.current.setShowAddPartnersModal(false);
  }, []);

  /**
   * Copies company size, sector, subsector, and branch filters from the partner modal onto the form.
   * @param {Object} partnerForm - Filters confirmed in the modal.
   * @returns {void}
   */
  const handlePartnersSuccess = useCallback((partnerForm) => {
    const current = apiRef.current;
    current.setFormData((prev) => ({
      ...prev,
      tamanosDeLaEmpresa: partnerForm.tamanosDeLaEmpresa,
      sectores: partnerForm.sectores,
      subSectores: partnerForm.subSectores,
      ramas: partnerForm.ramas,
    }));
    current.setShowAddPartnersModal(false);
    current.setHasChanges(true);
  }, []);

  return {
    handleImageChange,
    handleSubmit,
    handleInputChange,
    handleGoBack,
    handleHostChange,
    handleActivoChange,
    handleSociosAudience,
    handleNonMembersAudience,
    handleOpenPartners,
    handleClosePartners,
    handlePartnersSuccess,
  };
};

/**
 * Breakfast form state. `allowed` is false when the signed-in user cannot manage breakfast events;
 * the page renders nothing in that case.
 *
 * Edit mode comes from `location.state`. The init effect must not list `changePermissions`:
 * that function's identity changes often enough to reload the form while the user is typing.
 *
 * Host choices are users in role "13".
 *
 * An empty event name is treated as valid so the duplicate warning does not appear before the user types.
 * Once the breakfast event list has loaded, both the name effect and the name input re-check that list.
 *
 * Rejecting a banner clears the preview, the stored file name, and the MIME type together.
 * A successful read marks the form dirty and keeps the data URL on `imagenBanner`.
 *
 * Partner selection writes company sizes, sectors, subsectors, and branches onto the same form
 * and closes the modal. Those fields are not edited on this page itself.
 *
 * A create response navigates to the breakfast list with `eventCreated`.
 * An update of an existing id navigates with `eventUpdated`.
 * Anything else is logged and does not leave the page.
 */
const useBreakfastEventForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { changePermissions } = use(AppContext);
  const { allowed } = useRequireAccess(canManageBreakfastEvents);

  const [formData, setFormData] = useState(INITIAL_BREAKFAST_FORM);
  const [userHosts, setUserHosts] = useState([]);
  const [showError, setShowError] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [imageChanged, setImageChanged] = useState(false);
  const [imageFormat, setImageFormat] = useState(null);
  const [imageName, setImageName] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [eventData, setEventData] = useState(null);
  const [allBreakfastEvents, setAllBreakfastEvents] = useState([]);
  const [nameValidation, setNameValidation] = useState({ isValid: true });
  const [showAddPartnersModal, setShowAddPartnersModal] = useState(false);
  const imageRef = useRef(null);

  /**
   * Marks the banner drop zone while a file is dragged over it.
   * @returns {void}
   */
  const onDragEnter = useCallback(() => {
    imageRef.current?.classList.add(styles.dragOver);
  }, []);
  /**
   * Clears the banner drop-zone highlight when the drag leaves it.
   * @returns {void}
   */
  const onDragLeave = useCallback(() => {
    imageRef.current?.classList.remove(styles.dragOver);
  }, []);
  /**
   * Clears the banner drop-zone highlight after a drop. The file itself is read by the input change handler.
   * @returns {void}
   */
  const onDrop = useCallback(() => {
    imageRef.current?.classList.remove(styles.dragOver);
  }, []);

  useEffect(() => {
    if (!allowed) {
      return;
    }
    if (location.state?.isEditMode && location.state?.eventData) {
      setIsEditMode(true);
      setEventData(location.state.eventData);
    } else {
      setIsEditMode(false);
      setEventData(null);
    }

    const initializeComponent = async () => {
      if (!hasAuthToken()) {
        return;
      }
      const userId = localStorage.getItem("userId");
      const allEvents = await getAllBreakfastEvents(navigate);
      setAllBreakfastEvents(allEvents);
      const users = await getUsersFromSpecificRole(HOST_ROLE_ID);
      setUserHosts(users);
      await initializeBreakfastEventForm(
        location.state?.eventData?._id,
        userId,
        changePermissions,
        navigate,
        dayjs,
        setFormData,
        setLoading,
      );
    };

    initializeComponent();
  }, [allowed, location.state, navigate, location.pathname]);

  useEffect(() => {
    if (!String(formData.nombre ?? "").trim()) {
      setNameValidation({ isValid: true });
      return;
    }
    if (allBreakfastEvents.length > 0) {
      setNameValidation(
        validateBusinessEventName(
          formData.nombre,
          allBreakfastEvents,
          eventData?._id,
        ),
      );
    }
  }, [allBreakfastEvents, formData.nombre, eventData?._id]);

  const handlers = useBreakfastFormHandlers({
    t,
    navigate,
    formData,
    imageChanged,
    imageName,
    imageFormat,
    eventData,
    allBreakfastEvents,
    setError,
    setImageChanged,
    setFormData,
    setImageName,
    setImageFormat,
    setHasChanges,
    setShowError,
    setSaving,
    setEventData,
    setNameValidation,
    setShowAddPartnersModal,
  });

  const saveDisabled = isSaveDisabled({
    saving,
    loading,
    nameIsValid: nameValidation.isValid,
    isEditMode,
    hasChanges,
    formData,
  });

  return {
    allowed,
    viewProps: {
      t,
      navigate,
      loading,
      saving,
      formData,
      userHosts,
      imageRef,
      onDragEnter,
      onDragLeave,
      onDrop,
      isEditMode,
      error,
      showError,
      nameValidation,
      saveDisabled,
      showAddPartnersModal,
      ...handlers,
    },
  };
};

export default useBreakfastEventForm;
